import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSmartBack } from "@/lib/useSmartBack";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence, useAnimationControls } from "motion/react";
import { ArrowLeft, ArrowRight, Check, BookOpen, AtSign, Globe, Upload, FileText, X } from "lucide-react";
import { useCompanyId } from "@/lib/CompanyContext";
import { knowledgeApi, type KnowledgeBase, type OwnInstagramMeta } from "@/api/knowledge";
import { briefingApi, type BriefingAttachment } from "@/api/briefing";
import { queryKeys } from "@/lib/queryKeys";
import { Notice, RequiredMark, ColorChipsInput, BrandFontUploader } from "@/components/ui";
import type { BrandFontFile } from "@/api/product-library";
import { friendlyError } from "@/lib/friendlyError";

/**
 * Onboarding wizard — 5 passos pra coletar campos imutáveis da empresa
 * + dados pessoais do usuário. Salva como KnowledgeBase do tipo `onboarding`
 * (singleton — sempre injetada na Camada 2 do prompt dos agentes).
 *
 * Diferente do briefing antigo que tinha 7 passos com produtos/personas/
 * resultados misturados. Aqui SÓ tem o que não muda: identidade, voz,
 * MVV, e papel do usuário. Tudo modular vai como KBs separadas.
 */

interface OnboardingState {
  // Step 1 — Identidade
  nomeEmpresa: string;
  segmento: string;     // valor do select (ou "outro")
  segmentoOutro: string; // texto livre quando segmento === "outro"
  slogan: string;
  historia: string;

  // Step 2 — Posicionamento
  posicionamento: string;
  diferencial: string;
  credenciais: string[];

  // Step 3 — Voz
  tomVoz: string[];
  temasProibidos: string[];

  // Step 4 — MVV
  missao: string;
  visao: string;
  valores: string[];

  // Step 5 — MIV (Manual de Identidade Visual)
  // Camada base de identidade visual da MARCA. Produtos podem ter MIV
  // próprio (no briefing.products) — quando um produto é selecionado na
  // tarefa, a prioridade criativa é o MIV do produto.
  // Arquivos (logos, decks, etc) ficam em briefing_attachments com
  // category='miv-logo' ou 'miv'. State aqui só armazena cores/fontes/diretrizes.
  mivCores: string[];        // hex array — ex: ["#fe7000", "#0d1526"]
  mivTipografia: string[];   // ex: ["Bricolage Grotesque", "Inter"]
  mivFontFiles: BrandFontFile[]; // arquivos de fonte enviados (título/corpo)
  mivDiretrizes: string;     // diretrizes visuais em texto livre

  // Step 6 — Sobre você
  sobreUsuario: string;

  // Step 7 — Instagram (análise opcional do @ do cliente)
  instagramHandle: string;
}

const EMPTY: OnboardingState = {
  nomeEmpresa: "",
  segmento: "",
  segmentoOutro: "",
  slogan: "",
  historia: "",
  posicionamento: "",
  diferencial: "",
  credenciais: [],
  tomVoz: [],
  temasProibidos: [],
  missao: "",
  visao: "",
  valores: [],
  mivCores: [],
  mivTipografia: [],
  mivFontFiles: [],
  mivDiretrizes: "",
  sobreUsuario: "",
  instagramHandle: "",
};

const TONE_SUGGESTIONS = [
  "Profissional",
  "Descontraído",
  "Técnico",
  "Inspiracional",
  "Didático",
  "Direto ao ponto",
  "Provocador",
  "Acolhedor",
];

const STEPS = [
  { key: "comece", label: "Comece pelo @", subtitle: "A IA pré-preenche tudo, você só corrige" },
  { key: "identidade", label: "Identidade", subtitle: "Quem é a empresa" },
  { key: "posicionamento", label: "Posicionamento", subtitle: "O que diferencia vocês" },
  { key: "voz", label: "Voz", subtitle: "Como vocês falam" },
  { key: "mvv", label: "Missão / Visão / Valores", subtitle: "A bússola do negócio" },
  { key: "miv", label: "Identidade visual", subtitle: "Cores, fontes, logo e referências da marca" },
  { key: "voce", label: "Você", subtitle: "Seu papel e motivação" },
] as const;

function fromKb(kb: KnowledgeBase | null): OnboardingState {
  if (!kb) return EMPTY;
  const meta = kb.contentMeta?.onboarding ?? {};
  const ig = kb.contentMeta?.ownInstagram;
  const miv = meta.miv ?? {};
  return {
    nomeEmpresa: meta.nomeEmpresa ?? "",
    segmento: meta.segmento ?? "",
    segmentoOutro: meta.segmentoOutro ?? "",
    slogan: meta.slogan ?? "",
    historia: meta.historia ?? "",
    posicionamento: meta.posicionamento ?? "",
    diferencial: meta.diferencial ?? "",
    credenciais: meta.credenciais ?? [],
    tomVoz: meta.tomVoz ?? [],
    temasProibidos: meta.temasProibidos ?? [],
    missao: meta.missao ?? "",
    visao: meta.visao ?? "",
    valores: meta.valores ?? [],
    mivCores: miv.cores ?? [],
    mivTipografia: miv.tipografia ?? [],
    mivFontFiles: miv.fontFiles ?? [],
    mivDiretrizes: miv.diretrizes ?? "",
    sobreUsuario: meta.sobreUsuario ?? "",
    instagramHandle: ig?.handle ?? "",
  };
}

/** Renderiza a KB onboarding como texto plano que vai pro prompt */
function buildContentText(s: OnboardingState): string {
  const lines: string[] = ["# Sobre a empresa", ""];
  if (s.nomeEmpresa) lines.push(`**Nome:** ${s.nomeEmpresa}`);
  // Segmento: se for "outro", usa o texto livre. Senão, o label do select.
  const segLabel =
    s.segmento === "outro" && s.segmentoOutro.trim()
      ? s.segmentoOutro.trim()
      : s.segmento;
  if (segLabel) lines.push(`**Segmento:** ${segLabel}`);
  if (s.slogan) lines.push(`**Slogan:** ${s.slogan}`);
  if (s.historia) lines.push("", "**Contexto histórico:**", s.historia);
  if (s.posicionamento) lines.push("", "**Posicionamento:**", s.posicionamento);
  if (s.diferencial) lines.push("", "**Diferencial:**", s.diferencial);
  if (s.credenciais.length) lines.push("", "**Credenciais:**", ...s.credenciais.map((c) => `- ${c}`));
  if (s.tomVoz.length) lines.push("", `**Tom de voz:** ${s.tomVoz.join(", ")}`);
  if (s.temasProibidos.length)
    lines.push("", `**Temas proibidos:** ${s.temasProibidos.join(", ")}`);
  if (s.missao) lines.push("", "**Missão:**", s.missao);
  if (s.visao) lines.push("", "**Visão:**", s.visao);
  if (s.valores.length) lines.push("", "**Valores:**", ...s.valores.map((v) => `- ${v}`));

  // MIV — Manual de Identidade Visual da marca. Vira camada base pra agentes
  // criativos quando não há MIV de produto. Quando há produto selecionado na
  // tarefa, o MIV do produto sobrescreve este.
  // Arquivos (logos/decks) ficam em briefing_attachments — não vão pra cá.
  const hasMiv =
    s.mivCores.length > 0 ||
    s.mivTipografia.length > 0 ||
    s.mivDiretrizes.trim();
  if (hasMiv) {
    lines.push("", "# Identidade visual da marca");
    if (s.mivCores.length) lines.push("", `**Cores:** ${s.mivCores.join(", ")}`);
    if (s.mivTipografia.length)
      lines.push("", `**Tipografia:** ${s.mivTipografia.join(", ")}`);
    if (s.mivDiretrizes.trim())
      lines.push("", "**Diretrizes visuais:**", s.mivDiretrizes.trim());
  }

  if (s.sobreUsuario) lines.push("", "# Sobre o usuário", "", s.sobreUsuario);
  return lines.join("\n");
}

// ────────── primitives ──────────

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: "var(--wf-ink)",
          letterSpacing: "-0.005em",
        }}
      >
        {label}
        {required && <RequiredMark />}
      </span>
      {hint && (
        <span style={{ fontSize: 11, color: "var(--wf-ink-3)", marginTop: -3 }}>{hint}</span>
      )}
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  background: "var(--wf-paper)",
  border: "1px solid var(--wf-ink-5)",
  borderRadius: 8,
  padding: "9px 12px",
  fontSize: 14,
  fontFamily: "var(--wf-font-body)",
  color: "var(--wf-ink)",
  width: "100%",
  outline: "none",
  transition: "border-color 120ms ease, box-shadow 120ms ease",
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  minHeight: 96,
  resize: "vertical" as const,
  lineHeight: 1.5,
};

function ChipsInput({
  value,
  onChange,
  placeholder,
  suggestions,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  suggestions?: string[];
}) {
  const [draft, setDraft] = useState("");
  const add = (raw: string) => {
    const v = raw.trim();
    if (!v || value.includes(v)) return;
    onChange([...value, v]);
    setDraft("");
  };
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          padding: 8,
          minHeight: 44,
          border: "1px solid var(--wf-ink-5)",
          borderRadius: 8,
          background: "var(--wf-paper)",
        }}
      >
        {value.map((v, i) => (
          <span
            key={`${v}-${i}`}
            className="wf-pill wf-pill--orange"
            style={{ fontSize: 12, padding: "2px 8px", cursor: "pointer" }}
            onClick={() => remove(i)}
            title="Remover"
          >
            {v} ×
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add(draft);
            } else if (e.key === "Backspace" && !draft && value.length > 0) {
              remove(value.length - 1);
            }
          }}
          placeholder={value.length === 0 ? placeholder : ""}
          style={{
            flex: 1,
            minWidth: 120,
            border: "none",
            outline: "none",
            background: "transparent",
            fontSize: 13,
            fontFamily: "var(--wf-font-body)",
          }}
        />
      </div>
      {suggestions && suggestions.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          <span style={{ fontSize: 11, color: "var(--wf-ink-3)", marginRight: 4 }}>
            sugestões:
          </span>
          {suggestions
            .filter((s) => !value.includes(s))
            .map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => add(s)}
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  padding: "2px 8px",
                  border: "1px dashed var(--wf-ink-4)",
                  borderRadius: 999,
                  background: "transparent",
                  color: "var(--wf-ink-2)",
                  cursor: "pointer",
                }}
              >
                + {s}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

// ────────── steps ──────────

/** Tira "@", "http(s)://", "instagram.com/" etc. e retorna só o handle puro. */
function cleanInstagramHandle(raw: string): string {
  let h = raw.trim();
  // Se colou URL do IG, extrai o handle do path.
  const urlMatch = h.match(/instagram\.com\/+([^/?#]+)/i);
  if (urlMatch) h = urlMatch[1] ?? "";
  // Remove @ inicial repetido.
  h = h.replace(/^@+/, "");
  return h;
}

/**
 * Primeira etapa: dois caminhos pra pré-preencher o onboarding.
 *  - Pelo @ do Instagram (Apify + Sonnet)
 *  - Pelo site (Firecrawl + Sonnet) — reaproveita o briefing.analyzeUrl
 * Em qualquer um dos dois, os campos do wizard são preenchidos e o user
 * revisa nas próximas etapas. Sem botão "pular" — esse passo é obrigatório
 * (mas só aparece na PRIMEIRA vez, pra não gastar token à toa em retomadas).
 */
function StepComece({
  companyId,
  state,
  set,
  onAnalyzed,
  onIgBlob,
  onBusyChange,
}: {
  companyId: string;
  state: OnboardingState;
  set: (patch: Partial<OnboardingState>) => void;
  onAnalyzed: () => void;
  onIgBlob: (blob: OwnInstagramMeta) => void;
  onBusyChange?: (busy: boolean) => void;
}) {
  const [handle, setHandle] = useState(state.instagramHandle ?? "");
  const [siteUrl, setSiteUrl] = useState("");

  const analyzeIg = useMutation({
    mutationFn: () =>
      knowledgeApi.analyzeOnboardingFromInstagram(
        companyId,
        cleanInstagramHandle(handle),
      ),
    onSuccess: (res) => {
      const f = res.fields;
      set({
        instagramHandle: res.handle,
        nomeEmpresa: state.nomeEmpresa || f.nomeEmpresa,
        segmento: state.segmento || f.segmento,
        historia: state.historia || f.historia,
        posicionamento: state.posicionamento || f.posicionamento,
        diferencial: state.diferencial || f.diferencial,
        credenciais: state.credenciais.length ? state.credenciais : f.credenciais,
        tomVoz: state.tomVoz.length ? state.tomVoz : f.tomVoz,
        temasProibidos: state.temasProibidos.length
          ? state.temasProibidos
          : f.temasProibidos,
        missao: state.missao || f.missao,
        visao: state.visao || f.visao,
        valores: state.valores.length ? state.valores : f.valores,
      });
      onIgBlob(res.instagramMeta);
      onAnalyzed();
    },
  });

  const analyzeSite = useMutation({
    mutationFn: () => briefingApi.analyzeUrl(companyId, siteUrl.trim()),
    onSuccess: (res) => {
      // BriefingSuggestions tem o overlap com onboarding (nomeEmpresa, segmento,
      // historia, posicionamento, diferencial, credenciais, tomVoz). Missão/visão/
      // valores/temas proibidos ficam pra o user preencher nos passos seguintes.
      const f = res.suggestions;
      // Visual identity vem do mesmo scrape — pré-popula o step MIV.
      const vi = res.visualIdentity;
      set({
        nomeEmpresa: state.nomeEmpresa || f.nomeEmpresa || "",
        segmento: state.segmento || f.segmento || "",
        historia: state.historia || f.historia || "",
        posicionamento: state.posicionamento || f.posicionamento || "",
        diferencial: state.diferencial || f.diferencial || "",
        credenciais:
          state.credenciais.length ? state.credenciais : f.credenciais ?? [],
        tomVoz: state.tomVoz.length ? state.tomVoz : f.tomVoz ?? [],
        mivCores:
          state.mivCores.length
            ? state.mivCores
            : (vi?.colors ?? []).slice(0, 6).map((c) => c.toLowerCase()),
        mivTipografia:
          state.mivTipografia.length
            ? state.mivTipografia
            : (vi?.fonts ?? []).slice(0, 4),
      });
      onAnalyzed();
    },
  });

  const igBusy = analyzeIg.isPending;
  const siteBusy = analyzeSite.isPending;
  const anyBusy = igBusy || siteBusy;
  // Reporta "analisando" pro wizard travar o "Próximo" enquanto a IA roda (antes
  // dava pra avançar no meio da análise e perder o pré-preenchimento).
  useEffect(() => {
    onBusyChange?.(anyBusy);
  }, [anyBusy, onBusyChange]);
  // Feedback tátil/visual quando tenta analisar sem o @ do Instagram.
  const igShake = useAnimationControls();
  const [igHint, setIgHint] = useState(false);
  const igReady = cleanInstagramHandle(handle).length >= 2;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Caminho 1: Instagram */}
      <div
        style={{
          padding: 16,
          background: "var(--wf-paper)",
          border: "1px solid var(--wf-ink-5)",
          borderRadius: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 8,
          }}
        >
          <AtSign size={16} color="var(--nd-orange)" />
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            Pelo Instagram da marca
          </span>
        </div>
        <p
          style={{
            fontSize: 12,
            color: "var(--wf-ink-3)",
            lineHeight: 1.5,
            margin: 0,
            marginBottom: 12,
          }}
        >
          A IA lê bio + posts recentes e pré-preenche posicionamento, tom de voz,
          público, credenciais. Leva ~20s.
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1, position: "relative" }}>
            <span
              aria-hidden
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: 14,
                color: "var(--wf-ink-3)",
                pointerEvents: "none",
                fontFamily: "var(--wf-font-body)",
              }}
            >
              @
            </span>
            <input
              style={{ ...inputStyle, paddingLeft: 26 }}
              value={handle.replace(/^@+/, "")}
              onChange={(e) => setHandle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && cleanInstagramHandle(handle).length > 1) {
                  analyzeIg.mutate();
                }
              }}
              placeholder="novadimensaohub"
              disabled={anyBusy}
            />
          </div>
          <motion.button
            type="button"
            animate={igShake}
            aria-disabled={!igReady}
            onClick={() => {
              if (anyBusy) return;
              if (!igReady) {
                setIgHint(true);
                igShake.start({ x: [0, -6, 6, -4, 4, 0], transition: { duration: 0.38 } });
                return;
              }
              setIgHint(false);
              analyzeIg.mutate();
            }}
            className="wf-btn wf-btn--primary"
            style={{
              opacity: igReady && !anyBusy ? 1 : 0.6,
              cursor: anyBusy ? "wait" : igReady ? "pointer" : "not-allowed",
            }}
          >
            {igBusy ? "Analisando…" : "Pré-preencher"}
          </motion.button>
        </div>
        {igHint && !igReady && (
          <Notice tone="danger" style={{ marginTop: 8 }}>
            Digite o @ do Instagram pra eu analisar.
          </Notice>
        )}
        {analyzeIg.isError && (
          <Notice tone="danger" style={{ marginTop: 8 }}>
            {friendlyError((analyzeIg.error as Error).message)}
          </Notice>
        )}
      </div>

      {/* Divisor "ou" */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          margin: "2px 0",
        }}
      >
        <div style={{ flex: 1, height: 1, background: "var(--wf-ink-5)" }} />
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "var(--wf-ink-3)",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
          }}
        >
          ou
        </span>
        <div style={{ flex: 1, height: 1, background: "var(--wf-ink-5)" }} />
      </div>

      {/* Caminho 2: Site */}
      <div
        style={{
          padding: 16,
          background: "var(--wf-paper)",
          border: "1px solid var(--wf-ink-5)",
          borderRadius: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 8,
          }}
        >
          <Globe size={16} color="var(--nd-orange)" />
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            Pelo site da marca
          </span>
        </div>
        <p
          style={{
            fontSize: 12,
            color: "var(--wf-ink-3)",
            lineHeight: 1.5,
            margin: 0,
            marginBottom: 12,
          }}
        >
          A IA lê a home + páginas principais e pré-preenche os mesmos campos.
          Bom quando a marca tem mais conteúdo no site do que no IG.
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            style={{ ...inputStyle, flex: 1 }}
            value={siteUrl}
            onChange={(e) => setSiteUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && siteUrl.trim().length > 4) {
                analyzeSite.mutate();
              }
            }}
            placeholder="https://novadimensaohub.com.br"
            disabled={anyBusy}
          />
          <button
            type="button"
            onClick={() => analyzeSite.mutate()}
            disabled={anyBusy || siteUrl.trim().length < 4}
            className="wf-btn wf-btn--primary"
          >
            {siteBusy ? "Analisando…" : "Pré-preencher"}
          </button>
        </div>
        {analyzeSite.isError && (
          <Notice tone="danger" style={{ marginTop: 8 }}>
            {friendlyError((analyzeSite.error as Error).message)}
          </Notice>
        )}
      </div>
    </div>
  );
}

const SEGMENTO_OPTIONS = [
  { value: "servico", label: "Serviço" },
  { value: "varejo", label: "Varejo" },
  { value: "industria", label: "Indústria" },
  { value: "ecommerce", label: "E-commerce" },
  { value: "food_service", label: "Food service" },
  { value: "educacao", label: "Educação" },
  { value: "imobiliaria", label: "Imobiliária" },
  { value: "saas", label: "SaaS" },
  { value: "financas", label: "Finanças" },
  { value: "franquia", label: "Franquia" },
  { value: "telecom", label: "Telecom" },
  { value: "energia_solar", label: "Energia solar" },
  { value: "turismo", label: "Turismo" },
  { value: "outro", label: "Outro" },
] as const;

function StepIdentidade({
  state,
  set,
}: {
  state: OnboardingState;
  set: (patch: Partial<OnboardingState>) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Field label="Nome da empresa" required>
        <input
          style={inputStyle}
          value={state.nomeEmpresa}
          onChange={(e) => set({ nomeEmpresa: e.target.value })}
          placeholder="Ex: Nova Dimensão Digital"
        />
      </Field>
      <Field label="Segmento" hint="Em que mercado vocês atuam">
        <select
          style={inputStyle}
          value={state.segmento}
          onChange={(e) => set({ segmento: e.target.value })}
        >
          <option value="">— selecione —</option>
          {SEGMENTO_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {state.segmento === "outro" && (
          <input
            style={{ ...inputStyle, marginTop: 8 }}
            value={state.segmentoOutro}
            onChange={(e) => set({ segmentoOutro: e.target.value })}
            placeholder="Qual segmento?"
          />
        )}
      </Field>
      <Field label="Slogan" hint='Frase-tag da marca, se houver. Ex: "Just do it"'>
        <input
          style={inputStyle}
          value={state.slogan}
          onChange={(e) => set({ slogan: e.target.value })}
          placeholder="Ex: A escola que coloca você no jogo"
        />
      </Field>
      <Field
        label="Contexto histórico"
        hint="Resuma a história da sua empresa em poucas palavras."
      >
        <textarea
          style={textareaStyle}
          value={state.historia}
          onChange={(e) => set({ historia: e.target.value })}
          placeholder="Contexto, fundação, evolução, momento atual..."
        />
      </Field>
    </div>
  );
}

function StepPosicionamento({
  state,
  set,
}: {
  state: OnboardingState;
  set: (patch: Partial<OnboardingState>) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Field label="Posicionamento" hint="Como vocês querem ser percebidos no mercado" required>
        <textarea
          style={textareaStyle}
          value={state.posicionamento}
          onChange={(e) => set({ posicionamento: e.target.value })}
          placeholder="Ex: A escola de marketing digital mais prática do Brasil"
        />
      </Field>
      <Field label="Diferencial" hint="O que ninguém mais oferece como vocês">
        <textarea
          style={textareaStyle}
          value={state.diferencial}
          onChange={(e) => set({ diferencial: e.target.value })}
          placeholder="Ex: Único método com mentoria semanal de 1h pra cada aluno"
        />
      </Field>
      <Field
        label="Credenciais"
        hint="Conquistas, prêmios, certificações, números — tudo que dá autoridade"
      >
        <ChipsInput
          value={state.credenciais}
          onChange={(v) => set({ credenciais: v })}
          placeholder="Adicione e pressione Enter"
        />
      </Field>
    </div>
  );
}

function StepVoz({
  state,
  set,
}: {
  state: OnboardingState;
  set: (patch: Partial<OnboardingState>) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Field label="Tom de voz" hint="Como sua marca conversa com o público">
        <ChipsInput
          value={state.tomVoz}
          onChange={(v) => set({ tomVoz: v })}
          placeholder="Adicione tags"
          suggestions={TONE_SUGGESTIONS}
        />
      </Field>
      <Field
        label="Temas proibidos"
        hint="Assuntos que os agentes NUNCA devem tocar (política, religião, etc)"
      >
        <ChipsInput
          value={state.temasProibidos}
          onChange={(v) => set({ temasProibidos: v })}
          placeholder="Ex: política, religião"
        />
      </Field>
    </div>
  );
}

function StepMvv({
  state,
  set,
}: {
  state: OnboardingState;
  set: (patch: Partial<OnboardingState>) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Field label="Missão" hint="Por que a empresa existe">
        <textarea
          style={textareaStyle}
          value={state.missao}
          onChange={(e) => set({ missao: e.target.value })}
          placeholder="Ex: Democratizar o acesso a marketing digital de alta qualidade"
        />
      </Field>
      <Field label="Visão" hint="Onde vocês querem chegar">
        <textarea
          style={textareaStyle}
          value={state.visao}
          onChange={(e) => set({ visao: e.target.value })}
          placeholder="Ex: Ser referência nacional em educação prática até 2028"
        />
      </Field>
      <Field label="Valores" hint="Princípios que guiam decisões — 3 a 5 valores fortes">
        <ChipsInput
          value={state.valores}
          onChange={(v) => set({ valores: v })}
          placeholder="Ex: Honestidade, Excelência, Praticidade"
        />
      </Field>
    </div>
  );
}


/** Card de asset com thumbnail (imagem) ou ícone (PDF/PPT). Botão X pra remover. */
function MivAssetCard({
  attachment,
  onRemove,
  busy,
}: {
  attachment: BriefingAttachment;
  onRemove: () => void;
  busy?: boolean;
}) {
  const isImage = attachment.contentType?.startsWith("image/");
  const contentPath = `/api/assets/${attachment.assetId}/content`;
  const fmtSize = (n: number) =>
    n < 1024
      ? `${n} B`
      : n < 1024 * 1024
        ? `${Math.round(n / 1024)} KB`
        : `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return (
    <div
      style={{
        position: "relative",
        border: "1px solid var(--wf-ink-5)",
        borderRadius: 10,
        overflow: "hidden",
        background: "var(--wf-paper)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          width: "100%",
          aspectRatio: "1 / 1",
          background: "#f4f3ee",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {isImage ? (
          <img
            src={contentPath}
            alt={attachment.description ?? attachment.originalFilename ?? ""}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
              color: "var(--wf-ink-3)",
              fontSize: 11,
              padding: 12,
              textAlign: "center",
            }}
          >
            <FileText size={28} color="var(--nd-orange)" strokeWidth={1.5} />
            <span style={{ fontWeight: 600 }}>
              {attachment.contentType?.includes("pdf")
                ? "PDF"
                : attachment.contentType?.includes("presentation")
                  ? "PPT"
                  : "Arquivo"}
            </span>
          </div>
        )}
      </div>
      <div style={{ padding: 8, fontSize: 11, lineHeight: 1.4 }}>
        <div
          style={{
            fontWeight: 600,
            color: "var(--wf-ink)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {attachment.originalFilename ?? "arquivo"}
        </div>
        <div style={{ color: "var(--wf-ink-3)" }}>
          {fmtSize(attachment.byteSize)}
        </div>
      </div>
      <button
        type="button"
        onClick={onRemove}
        disabled={busy}
        title="Remover"
        style={{
          position: "absolute",
          top: 6,
          right: 6,
          width: 24,
          height: 24,
          borderRadius: "50%",
          background: "rgba(0,0,0,0.55)",
          color: "#fff",
          border: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
}

/** "Card" de upload que abre o file picker ao clicar. */
function MivUploadCard({ onClick, busy }: { onClick: () => void; busy?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      style={{
        aspectRatio: "1 / 1",
        border: "1.5px dashed var(--wf-ink-5)",
        borderRadius: 10,
        background: "var(--wf-paper)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        cursor: busy ? "not-allowed" : "pointer",
        color: "var(--wf-ink-3)",
        fontSize: 12,
        fontFamily: "var(--wf-font-body)",
        opacity: busy ? 0.6 : 1,
      }}
    >
      <Upload size={22} strokeWidth={1.5} color="var(--nd-orange)" />
      <span style={{ fontWeight: 600, color: "var(--wf-ink-2)" }}>
        {busy ? "Enviando…" : "Adicionar"}
      </span>
    </button>
  );
}

/** Grid de uploads MIV — usado pra logos (só imagens) e pra materiais
 *  (imagens + PDF + PPT). Igual padrão produto: thumbnails com remover, card
 *  de adicionar no fim. */
function MivAttachmentsGrid({
  companyId,
  category,
  accept,
  emptyHint,
}: {
  companyId: string;
  category: "miv-logo" | "miv";
  accept: string;
  emptyHint: string;
}) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const queryKey = ["companies", companyId, "briefing", "attachments", category];
  const listQuery = useQuery({
    queryKey,
    queryFn: () => briefingApi.listAttachments(companyId, category),
  });

  const upload = useMutation({
    mutationFn: (file: File) =>
      briefingApi.uploadAttachment(companyId, file, { category }),
    onSuccess: () => {
      if (fileInputRef.current) fileInputRef.current.value = "";
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => briefingApi.deleteAttachment(companyId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    for (const file of Array.from(files)) {
      await upload.mutateAsync(file).catch((err) => {
        alert(`Falha no upload: ${(err as Error).message}`);
      });
    }
  };

  const items = listQuery.data ?? [];

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple
        style={{ display: "none" }}
        onChange={(e) => handleFiles(e.target.files)}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
          gap: 10,
        }}
      >
        {items.map((att) => (
          <MivAssetCard
            key={att.id}
            attachment={att}
            onRemove={() => remove.mutate(att.id)}
            busy={remove.isPending}
          />
        ))}
        <MivUploadCard
          onClick={() => fileInputRef.current?.click()}
          busy={upload.isPending}
        />
      </div>
      {items.length === 0 && (
        <p style={{ fontSize: 11, color: "var(--wf-ink-3)", marginTop: 8 }}>
          {emptyHint}
        </p>
      )}
      {upload.isError && (
        <p style={{ fontSize: 12, color: "#c00", marginTop: 6 }}>
          {(upload.error as Error).message}
        </p>
      )}
    </div>
  );
}

function StepMiv({
  state,
  set,
  companyId,
}: {
  state: OnboardingState;
  set: (patch: Partial<OnboardingState>) => void;
  companyId: string;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Field
        label="Cores da marca"
        hint="Hex das cores principais (ex: #fe7000). Enter ou vírgula pra adicionar"
      >
        <ColorChipsInput
          value={state.mivCores}
          onChange={(v) => set({ mivCores: v })}
        />
      </Field>
      <Field
        label="Tipografia"
        hint="Fontes principais — títulos, corpo, etc."
      >
        <ChipsInput
          value={state.mivTipografia}
          onChange={(v) => set({ mivTipografia: v })}
          placeholder="Ex: Bricolage Grotesque, Inter, Satoshi"
        />
      </Field>
      <Field
        label="Arquivos de fonte (recomendado)"
        hint="Suba o arquivo .ttf/.otf/.woff da fonte de título e de corpo. Sem isso, a arte sai na fonte padrão (Bricolage) quando a sua não é uma fonte gratuita do Google."
      >
        <BrandFontUploader
          companyId={companyId}
          value={state.mivFontFiles}
          onChange={(v) => set({ mivFontFiles: v })}
        />
      </Field>
      <Field
        label="Logos"
        hint="Suba PNG/SVG/JPG do logo. Pode ter variantes (principal, branco, ícone). Os agentes não inventam logo — só usam o que você anexar aqui."
      >
        <MivAttachmentsGrid
          companyId={companyId}
          category="miv-logo"
          accept="image/*"
          emptyHint="Nenhum logo ainda — clique em Adicionar pra subir o(s) arquivo(s)."
        />
      </Field>
      <Field
        label="Materiais visuais (deck, manual, PPT, PDF, imagens)"
        hint="Manual da marca, deck institucional, refs visuais. Tudo que os agentes criativos podem consultar como referência."
      >
        <MivAttachmentsGrid
          companyId={companyId}
          category="miv"
          accept=".pdf,.ppt,.pptx,image/*,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
          emptyHint="Nenhum material ainda — clique em Adicionar pra subir arquivos."
        />
      </Field>
      <Field
        label="Diretrizes visuais"
        hint="Tom visual, estilo de imagem, o que evitar — fica de orientação pros agentes criativos"
      >
        <textarea
          style={{ ...textareaStyle, minHeight: 120 }}
          value={state.mivDiretrizes}
          onChange={(e) => set({ mivDiretrizes: e.target.value })}
          placeholder="Ex: Estética clean e moderna, muito espaço em branco. Evitar fotos de banco. Sempre logo no canto superior esquerdo."
        />
      </Field>
    </div>
  );
}

function StepVoce({
  state,
  set,
}: {
  state: OnboardingState;
  set: (patch: Partial<OnboardingState>) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Field
        label="Sobre você"
        hint="Como você gosta de receber as coisas — os agentes adaptam o jeito de falar/entregar."
      >
        <textarea
          style={{ ...textareaStyle, minHeight: 180 }}
          value={state.sobreUsuario}
          onChange={(e) => set({ sobreUsuario: e.target.value })}
          placeholder={`Conte sobre você de um jeito que ajude os agentes a te entender.

• Como você pensa: analítico (com números/dados) ou intuitivo (rápido, no feeling)?
• Como gosta de ler conteúdo: direto ao ponto ou com contexto/storytelling?
• Comunicativo, reservado, didático, provocador?
• Toma decisão sozinho ou com a equipe? É detalhista ou prefere visão geral?
• Seu papel na empresa e o que mais te ajuda no dia a dia.

Quanto mais específico, mais os agentes acertam o tom da entrega.`}
        />
      </Field>
    </div>
  );
}


// ────────── main wizard ──────────

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const goBack = useSmartBack("/briefing");
  const companyId = useCompanyId();
  const queryClient = useQueryClient();
  const [stepIdx, setStepIdx] = useState(0);
  // Mostra o motivo de não avançar quando faltam campos obrigatórios.
  const [showRequiredHint, setShowRequiredHint] = useState(false);
  // "Analisando @/site" no passo Comece — trava o avanço enquanto a IA roda.
  const [comeceBusy, setComeceBusy] = useState(false);
  const [state, setState] = useState<OnboardingState>(EMPTY);
  // Auto-save: indicador "Salvo às HH:MM" + baselines pra detectar mudança.
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const hydratedRef = useRef(false);
  const savedSigRef = useRef<string | null>(null);
  const sentSigRef = useRef<string | null>(null);
  // ownInstagram já persistido (com topPostAssetIds, se baixados). O auto-save
  // reenvia ESTE blob — não o fresco do step 1 — pra não re-disparar o download
  // das imagens a cada save (o `update` substitui o contentMeta inteiro).
  const persistedIgRef = useRef<OwnInstagramMeta | null>(null);

  const existing = useQuery({
    queryKey: companyId
      ? queryKeys.companies.knowledgeOnboarding(companyId)
      : ["kb-onboarding-empty"],
    queryFn: () => knowledgeApi.getOnboarding(companyId!),
    enabled: !!companyId,
  });

  // Hydrate UMA vez só (quando a query resolve). Se refetchasse e re-hidratasse
  // a cada invalidação, sobrescreveria o que o usuário está digitando. Também
  // fixa o baseline do auto-save (assinatura do estado já persistido).
  useEffect(() => {
    if (hydratedRef.current || existing.isLoading) return;
    if (existing.data) {
      const s = fromKb(existing.data);
      setState(s);
      savedSigRef.current = JSON.stringify(s);
      persistedIgRef.current = existing.data.contentMeta?.ownInstagram ?? null;
    } else {
      savedSigRef.current = JSON.stringify(EMPTY);
    }
    hydratedRef.current = true;
  }, [existing.data, existing.isLoading]);

  // Blob da análise de IG produzido no step 1 ("Comece pelo @"). Guardado
  // pra ser persistido no save sem re-scrape. Se o user pulou o step 1, fica
  // null e nenhuma análise de IG é feita.
  const [igBlob, setIgBlob] = useState<OwnInstagramMeta | null>(null);

  // Payload do onboarding KB (compartilhado entre o save explícito e o
  // auto-save). O backend faz upsert pro singleton de onboarding, então chamar
  // várias vezes é idempotente (não cria duplicados). `ig` é o blob ownInstagram
  // a persistir — o save explícito manda o fresco do step 1 (dispara o download
  // 1×); o auto-save manda só o já persistido (com assetIds → guard pula).
  const buildOnboardingInput = (ig: OwnInstagramMeta | null) => ({
    type: "onboarding" as const,
    name: state.nomeEmpresa || "Onboarding",
    description: state.posicionamento || null,
    sourceKind: "manual" as const,
    contentText: buildContentText(state),
    contentMeta: {
      onboarding: {
        nomeEmpresa: state.nomeEmpresa,
        segmento: state.segmento,
        segmentoOutro: state.segmentoOutro,
        slogan: state.slogan,
        historia: state.historia,
        posicionamento: state.posicionamento,
        diferencial: state.diferencial,
        credenciais: state.credenciais,
        tomVoz: state.tomVoz,
        temasProibidos: state.temasProibidos,
        missao: state.missao,
        visao: state.visao,
        valores: state.valores,
        miv: {
          cores: state.mivCores,
          tipografia: state.mivTipografia,
          fontFiles: state.mivFontFiles,
          diretrizes: state.mivDiretrizes,
        },
        sobreUsuario: state.sobreUsuario,
      },
      ...(ig ? { ownInstagram: ig } : {}),
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      if (!companyId) throw new Error("no company");
      // Save explícito: persiste o blob fresco do step 1 (se houver), senão o
      // já persistido. É aqui que o download das imagens é disparado (1×).
      return knowledgeApi.create(companyId, buildOnboardingInput(igBlob ?? persistedIgRef.current));
    },
    onSuccess: () => {
      if (companyId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.companies.knowledge(companyId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.companies.knowledgeOnboarding(companyId),
        });
      }
      navigate("/briefing");
    },
  });

  // Auto-save silencioso: mesmo payload (upsert), MAS sem navegar e sem
  // invalidar a query de onboarding (refetch re-hidrataria e apagaria a edição
  // em andamento). Só atualiza o indicador "Salvo às HH:MM".
  const draftSave = useMutation({
    mutationFn: async () => {
      if (!companyId) throw new Error("no company");
      sentSigRef.current = JSON.stringify(state);
      // Auto-save NÃO empurra o blob fresco do IG — só o já persistido (com
      // assetIds) — pra não re-baixar imagens a cada save. O blob fresco do
      // step 1 é persistido no save explícito ("Concluir"/"Salvar e sair").
      return knowledgeApi.create(companyId, buildOnboardingInput(persistedIgRef.current));
    },
    onSuccess: () => {
      savedSigRef.current = sentSigRef.current;
      setLastSavedAt(new Date());
    },
  });

  // Dispara o auto-save 2s depois da última edição (debounce reinicia a cada
  // mudança de `state`). Só quando já hidratou, há mudança real e nenhum save
  // em voo. Resolve a dor #1 do beta: trocar de step/aba e perder o preenchido.
  useEffect(() => {
    if (!hydratedRef.current || draftSave.isPending || save.isPending) return;
    if (JSON.stringify(state) === savedSigRef.current) return;
    const t = setTimeout(() => draftSave.mutate(), 2000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, draftSave.isPending, save.isPending]);

  // "Salvo às HH:MM" some depois de 3s (volta pro estado neutro).
  useEffect(() => {
    if (!lastSavedAt) return;
    const t = setTimeout(() => setLastSavedAt(null), 3000);
    return () => clearTimeout(t);
  }, [lastSavedAt]);

  const set = (patch: Partial<OnboardingState>) => setState((prev) => ({ ...prev, ...patch }));

  // Já tem onboarding salvo? Então o passo "Comece pelo @" some — pra não
  // gastar token analisando IG/site de novo numa retomada. User reabre o
  // wizard só pra editar campos a partir de "Identidade".
  const hasExistingOnboarding = useMemo(() => {
    const meta = existing.data?.contentMeta?.onboarding;
    if (!meta) return false;
    return Boolean(
      (meta.nomeEmpresa && meta.nomeEmpresa.trim()) ||
        (meta.posicionamento && meta.posicionamento.trim()) ||
        existing.data?.contentMeta?.ownInstagram,
    );
  }, [existing.data]);

  // "comece" (análise do @/site) fica SEMPRE visível pra dar pra REFAZER a 1ª
  // etapa (antes era filtrado numa retomada e o passo ficava inacessível — o
  // fluxo confundia). Numa retomada o cursor começa em "Identidade" (efeito
  // abaixo), mas o usuário pode voltar pro "comece" pela navegação/Voltar.
  const visibleSteps = STEPS;

  // Numa retomada (onboarding já existe), abre direto em "Identidade" pra não
  // empurrar a análise do @ de novo — mas o "comece" continua acessível. Roda 1×.
  const retakeAlignedRef = useRef(false);
  useEffect(() => {
    if (retakeAlignedRef.current || existing.isLoading) return;
    if (hasExistingOnboarding) {
      const idx = STEPS.findIndex((s) => s.key === "identidade");
      if (idx > 0) setStepIdx(idx);
    }
    retakeAlignedRef.current = true;
  }, [hasExistingOnboarding, existing.isLoading]);

  // Se o stepIdx atual ficou fora dos visíveis, realinha pra 0.
  useEffect(() => {
    if (stepIdx > visibleSteps.length - 1) setStepIdx(0);
  }, [visibleSteps.length, stepIdx]);

  const step = visibleSteps[stepIdx] ?? visibleSteps[0]!;
  const isLast = stepIdx === visibleSteps.length - 1;
  const isFirst = stepIdx === 0;

  // Campos OBRIGATÓRIOS do onboarding (impeditivos pra avançar/concluir). Os
  // demais são enriquecimento. Marcados com * vermelho no Field.
  const REQUIRED_FIELDS: Array<{ stepKey: string; field: keyof OnboardingState; label: string }> = [
    { stepKey: "identidade", field: "nomeEmpresa", label: "Nome da empresa" },
    { stepKey: "posicionamento", field: "posicionamento", label: "Posicionamento" },
  ];
  const missingRequired = (forStepKey?: string) =>
    REQUIRED_FIELDS.filter(
      (r) => (!forStepKey || r.stepKey === forStepKey) && !String(state[r.field] ?? "").trim(),
    );
  const stepMissing = missingRequired(step.key);
  const allMissing = missingRequired();
  // Esconde o aviso ao trocar de passo (some sozinho quando preenche também).
  useEffect(() => {
    setShowRequiredHint(false);
  }, [stepIdx]);

  const stepContent = useMemo(() => {
    switch (step.key) {
      case "comece":
        return (
          <StepComece
            companyId={companyId!}
            state={state}
            set={set}
            onAnalyzed={() => setStepIdx(1)}
            onIgBlob={setIgBlob}
            onBusyChange={setComeceBusy}
          />
        );
      case "identidade":
        return <StepIdentidade state={state} set={set} />;
      case "posicionamento":
        return <StepPosicionamento state={state} set={set} />;
      case "voz":
        return <StepVoz state={state} set={set} />;
      case "mvv":
        return <StepMvv state={state} set={set} />;
      case "miv":
        return <StepMiv state={state} set={set} companyId={companyId!} />;
      case "voce":
        return <StepVoce state={state} set={set} />;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.key, state, companyId]);

  const progress = ((stepIdx + 1) / visibleSteps.length) * 100;

  if (!companyId) {
    return (
      <div style={{ padding: 32 }}>
        <span className="wf-hand-note">carregando empresa…</span>
      </div>
    );
  }

  return (
    <div>
      {/* Hero compacto */}
      <div
        style={{
          background: "linear-gradient(90deg, #050810 0%, #0a1020 50%, #1a1424 100%)",
          color: "#fff",
          padding: "32px 32px 24px",
          borderBottom: "1px solid var(--wf-ink-5)",
        }}
      >
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 11,
              fontWeight: 600,
              color: "rgba(255,255,255,0.55)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              marginBottom: 10,
            }}
          >
            <BookOpen size={13} strokeWidth={1.75} color="var(--nd-orange)" />
            Onboarding · Base de Conhecimento
          </div>
          <h1
            className="wf-display"
            style={{
              fontSize: 26,
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
              margin: 0,
            }}
          >
            {step.label}
          </h1>
          <p
            style={{
              fontSize: 13,
              color: "rgba(255,255,255,0.65)",
              marginTop: 6,
              fontWeight: 500,
            }}
          >
            {step.subtitle}
          </p>

          {/* Progress + step nav */}
          <div style={{ marginTop: 18 }}>
            <div
              style={{
                height: 3,
                background: "rgba(255,255,255,0.1)",
                borderRadius: 999,
                overflow: "hidden",
                marginBottom: 10,
              }}
            >
              <motion.div
                style={{
                  height: "100%",
                  background: "var(--nd-orange)",
                  borderRadius: 999,
                }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              />
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              {visibleSteps.map((s, i) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setStepIdx(i)}
                  style={{
                    fontSize: 11,
                    fontWeight: i === stepIdx ? 600 : 400,
                    padding: "4px 10px",
                    borderRadius: 999,
                    border: "none",
                    background:
                      i === stepIdx ? "rgba(255,112,0,0.15)" : "transparent",
                    color:
                      i === stepIdx
                        ? "var(--nd-orange)"
                        : i < stepIdx
                        ? "rgba(255,255,255,0.55)"
                        : "rgba(255,255,255,0.35)",
                    cursor: "pointer",
                    fontFamily: "var(--wf-font-body)",
                    letterSpacing: "0.01em",
                  }}
                >
                  {i + 1}. {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "28px 32px 48px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={step.key}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            >
              {stepContent}
            </motion.div>
          </AnimatePresence>

          {/* Nav buttons */}
          <div
            style={{
              display: "flex",
              gap: 10,
              marginTop: 28,
              paddingTop: 16,
              borderTop: "1px solid var(--wf-ink-5)",
            }}
          >
            <button
              type="button"
              className="wf-btn wf-btn--ghost wf-btn--sm"
              onClick={() => {
                // Primeira tela: sai do wizard pra PÁGINA ANTERIOR (de onde veio).
                // Senão volta um step. Antes ficava disabled na primeira tela e o
                // user não tinha como sair sem completar o fluxo.
                if (isFirst) {
                  goBack();
                } else {
                  setStepIdx((i) => Math.max(0, i - 1));
                }
              }}
              style={{ display: "flex", alignItems: "center", gap: 5 }}
            >
              <ArrowLeft size={12} strokeWidth={2} /> Voltar
            </button>
            <div style={{ flex: 1 }} />
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 11,
                color: draftSave.isPending
                  ? "var(--wf-ink-3)"
                  : lastSavedAt
                    ? "#1a8754"
                    : "var(--wf-ink-3)",
                fontWeight: lastSavedAt ? 600 : 400,
              }}
            >
              {draftSave.isPending ? (
                "Salvando…"
              ) : lastSavedAt ? (
                <>
                  <Check size={12} strokeWidth={2.25} />
                  {`Salvo às ${lastSavedAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`}
                </>
              ) : (
                "salva automaticamente"
              )}
            </span>
            <button
              type="button"
              className="wf-btn wf-btn--ghost wf-btn--sm"
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? "Salvando…" : "Salvar e sair depois"}
            </button>
            {isLast ? (
              <button
                type="button"
                className="wf-btn wf-btn--primary"
                disabled={save.isPending}
                onClick={() => {
                  if (allMissing.length > 0) {
                    // Leva a pessoa pro passo do 1º campo faltante e explica.
                    const target = visibleSteps.findIndex((s) => s.key === allMissing[0]!.stepKey);
                    if (target >= 0) setStepIdx(target);
                    setShowRequiredHint(true);
                    return;
                  }
                  setShowRequiredHint(false);
                  save.mutate();
                }}
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                {save.isPending ? "Salvando…" : "Concluir"}
                <Check size={13} strokeWidth={2} color="#fff" />
              </button>
            ) : (
              <button
                type="button"
                className="wf-btn wf-btn--primary"
                disabled={comeceBusy}
                onClick={() => {
                  if (stepMissing.length > 0) {
                    setShowRequiredHint(true);
                    return;
                  }
                  setShowRequiredHint(false);
                  setStepIdx((i) => Math.min(visibleSteps.length - 1, i + 1));
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  opacity: comeceBusy ? 0.55 : 1,
                  cursor: comeceBusy ? "not-allowed" : "pointer",
                }}
              >
                {comeceBusy ? "Analisando…" : "Próximo"}
                {!comeceBusy && <ArrowRight size={13} strokeWidth={2} color="#fff" />}
              </button>
            )}
          </div>

          {showRequiredHint && (isLast ? allMissing : stepMissing).length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Notice tone="danger" title="Faltou preencher">
                Preencha {(isLast ? allMissing : stepMissing).length === 1 ? "o campo obrigatório" : "os campos obrigatórios"}{" "}
                (marcados com <span style={{ color: "#e5484d", fontWeight: 700 }}>*</span>):{" "}
                {(isLast ? allMissing : stepMissing).map((m) => m.label).join(", ")}.
              </Notice>
            </div>
          )}

          {save.isError && (
            <div
              style={{
                marginTop: 12,
                padding: 10,
                background: "#fff0ee",
                border: "1px solid #ff5577",
                borderRadius: 8,
                color: "#cc3344",
                fontSize: 12,
              }}
            >
              Erro ao salvar: {(save.error as Error)?.message ?? "tenta de novo"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
